import { useState } from "react";
import { toast } from "sonner";
import { Bug, LifeBuoy, Send, Mail } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { btn } from "@/components/Page";

const SUPPORT_EMAIL = "admin@lanistamanager.com";

export default function ContactSupport() {
  const [kind, setKind] = useState("bug");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (message.trim().length < 5) return toast.error("Please describe it in a little more detail");
    setBusy(true);
    try {
      const r = await api.post("/feedback", { kind, subject: subject.trim() || null, message: message.trim() });
      toast.success(r.data.message || "Message sent!");
      setSubject(""); setMessage("");
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const tab = (id, label, Icon) => (
    <button
      data-testid={`contact-tab-${id}`}
      onClick={() => setKind(id)}
      className={`flex items-center gap-2 px-4 py-2 text-sm font-cinzel uppercase tracking-wide border-b-2 -mb-px transition-colors ${kind === id ? "border-amber-500 text-amber-300" : "border-transparent text-zinc-500 hover:text-amber-300"}`}
    >
      <Icon size={15} /> {label}
    </button>
  );

  return (
    <section data-testid="contact-support" className="mt-10 border border-amber-900/30 bg-zinc-950 p-6">
      <div className="flex items-center gap-2 mb-1">
        <LifeBuoy size={18} className="text-amber-400" />
        <h2 className="font-cinzel text-amber-300 uppercase tracking-wide text-lg">Help &amp; Feedback</h2>
      </div>
      <p className="text-sm text-zinc-500 mb-5 flex items-center gap-1.5">
        <Mail size={13} /> Messages reach our team at <span className="text-amber-300/90">{SUPPORT_EMAIL}</span>
      </p>

      <div className="flex gap-1 border-b border-zinc-800 mb-4">
        {tab("bug", "Report a Bug", Bug)}
        {tab("support", "Contact Support", LifeBuoy)}
      </div>

      <div className="space-y-3 max-w-2xl">
        <input
          data-testid="contact-subject"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          maxLength={160}
          placeholder={kind === "bug" ? "Short summary (e.g. Training schedule won't save)" : "What do you need help with?"}
          className="w-full bg-zinc-900 border border-zinc-800 px-3 py-2.5 text-zinc-100 text-sm focus:border-amber-600 outline-none rounded-sm"
        />
        <textarea
          data-testid="contact-message"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          rows={5}
          maxLength={4000}
          placeholder={kind === "bug"
            ? "Describe the bug, what you expected, and the steps to reproduce it."
            : "Describe your question or issue and we'll get back to you."}
          className="w-full bg-zinc-900 border border-zinc-800 px-3 py-2.5 text-zinc-100 text-sm focus:border-amber-600 outline-none resize-y rounded-sm"
        />
        <button data-testid="contact-submit" disabled={busy} onClick={submit} className={btn("primary") + " flex items-center gap-2"}>
          <Send size={15} /> {busy ? "Sending…" : kind === "bug" ? "Send Bug Report" : "Send Message"}
        </button>
      </div>
    </section>
  );
}
